import { Bell, CheckCircle2, ExternalLink, RotateCw, XCircle } from "lucide-react";
import { useEffect, useState } from "react";

import type { NotificationDto } from "../../shared/types";
import { fmtRelativeTime } from "../lib/format";
import { navigate } from "../router";
import { Button, EmptyState, Spinner, StateLabel } from "../ui";
import { metaFor } from "./EventTypeIcon";
import { Modal } from "./Modal";

type AlertsModalProps = {
	open: boolean;
	onClose: () => void;
};

const PAGE_SIZE = 50;

async function fetchNotifications(before?: number): Promise<NotificationDto[]> {
	const qs = new URLSearchParams({ limit: String(PAGE_SIZE) });
	if (before != null) qs.set("before", String(before));
	const res = await fetch(`/api/notifications?${qs}`);
	const data = (await res.json()) as { notifications: NotificationDto[] };
	return data.notifications;
}

export function AlertsModal({ open, onClose }: AlertsModalProps) {
	const [items, setItems] = useState<NotificationDto[]>([]);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [endReached, setEndReached] = useState(false);

	const refresh = async () => {
		setLoading(true);
		setEndReached(false);
		try {
			const rows = await fetchNotifications();
			setItems(rows);
			if (rows.length < PAGE_SIZE) setEndReached(true);
		} finally {
			setLoading(false);
		}
	};

	const loadMore = async () => {
		if (loadingMore || endReached || items.length === 0) return;
		setLoadingMore(true);
		try {
			const oldest = items[items.length - 1]!;
			const rows = await fetchNotifications(oldest.id);
			setItems((prev) => [...prev, ...rows]);
			if (rows.length < PAGE_SIZE) setEndReached(true);
		} finally {
			setLoadingMore(false);
		}
	};

	useEffect(() => {
		if (open) refresh();
	}, [open]);

	const sentCount = items.filter((n) => n.status === "sent").length;
	const failedCount = items.filter((n) => n.status === "failed").length;

	return (
		<Modal
			open={open}
			onClose={onClose}
			title="Discord notifications"
			subtitle={items.length > 0 ? `${sentCount} sent · ${failedCount} failed` : undefined}
			rightHeader={
				<button
					onClick={refresh}
					disabled={loading}
					className="flex size-tap items-center justify-center rounded-md text-muted hover:bg-raised hover:text-ink disabled:opacity-50"
					aria-label="Refresh"
				>
					<RotateCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} aria-hidden />
				</button>
			}
		>
			<div className="min-h-0 flex-1 overflow-y-auto">
				{loading && items.length === 0 ? (
					<div className="flex items-center justify-center gap-2 px-4 py-8 text-[13px] text-muted">
						<Spinner /> Loading…
					</div>
				) : items.length === 0 ? (
					<div className="p-3">
						<EmptyState
							icon={Bell}
							title="No Discord notifications yet."
							description="They appear here as events fire matching your rules."
							action={{
								label: "Configure Discord",
								onClick: () => {
									onClose();
									navigate("/settings");
								},
							}}
						/>
					</div>
				) : (
					<ul className="divide-y divide-hairline">
						{items.map((n) => (
							<NotificationItem key={n.id} n={n} />
						))}
						{!endReached && (
							<li className="px-3 py-2 text-center">
								<Button variant="secondary" size="sm" onClick={loadMore} disabled={loadingMore}>
									{loadingMore ? "Loading…" : "Load older"}
								</Button>
							</li>
						)}
					</ul>
				)}
			</div>
		</Modal>
	);
}

function NotificationItem({ n }: { n: NotificationDto }) {
	const meta = n.eventType ? metaFor(n.eventType) : null;
	const ts = n.sentAt ?? n.eventCreatedAt;
	const sent = n.status === "sent";

	return (
		<li className="flex items-start gap-2.5 px-3 py-2 hover:bg-raised">
			<div className="mt-[3px] shrink-0">
				{sent ? (
					<CheckCircle2 className="size-3.5 text-ink" aria-label="sent" />
				) : (
					<XCircle className="size-3.5 text-muted" aria-label="failed" />
				)}
			</div>
			<div className="min-w-0 flex-1">
				<div className="truncate text-[13px] text-ink">
					{n.productTitle ?? <span className="text-muted">(product unavailable)</span>}
				</div>
				<div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px]">
					{meta && (
						<span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 ${meta.chip}`}>
							<meta.Icon className="size-3" aria-hidden />
							{meta.label}
						</span>
					)}
					{n.storeId && <span className="rounded-full bg-raised px-2 py-0.5 font-medium text-muted">{n.storeId}</span>}
					{n.ruleLabel && (
						<span className="rounded-full border border-hairline px-2 py-px text-muted">{n.ruleLabel}</span>
					)}
					{ts != null && <span className="text-muted">{fmtRelativeTime(ts)}</span>}
					{!sent && <StateLabel state="fault" label="Failed" />}
				</div>
				{!sent && n.error && <div className="mt-0.5 text-xs text-muted">{n.error}</div>}
			</div>
			{n.productUrl && (
				<a
					href={n.productUrl}
					target="_blank"
					rel="noreferrer"
					className="flex size-tap shrink-0 items-center justify-center rounded-md text-muted hover:bg-paper hover:text-ink"
					aria-label="Open product"
				>
					<ExternalLink className="size-3.5" aria-hidden />
				</a>
			)}
		</li>
	);
}
